/**
 * Search's merged Location facet + results map (spec-search-map-merge.md).
 * Replaces the old standalone Map tab (MapPage.tsx, deleted) and the old
 * MiniLocationPicker (map_center-only reference picker) with a single
 * component that does both jobs: shows the current type's matching
 * Event/Action/Group pins (clustered), and — only when locationMode is
 * 'map_center' — doubles as the draggable center-reference picker via the
 * same fixed-CSS-pin-over-moveend technique MiniLocationPicker used.
 *
 * Collapsed by default (a full-width button with a small inert map preview
 * as its background); auto-expands when the caller's locationMode becomes
 * 'address' or 'map_center' (SearchPage drives that), but the user can
 * still manually collapse/expand at any time.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import L from 'leaflet'
import { renderToStaticMarkup } from 'react-dom/server'
import { Zap, Calendar, Users, Lock, MapPin as MapPinIcon, Map as MapIcon, LocateFixed, Loader2 } from 'lucide-react'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import { formatDateTime } from '@/lib/dates'
import { useToast } from '@/components/common/Toast'
import type { MapPin } from '@olimhub/shared'

const ISRAEL_DEFAULT_ZOOM = 8
const PREVIEW_ZOOM = 7
const LOCATE_ME_ZOOM = 14

function useInvalidateSizeOnResize(mapRef: React.RefObject<L.Map | null>, wrapperRef: React.RefObject<HTMLElement | null>) {
  // Leaflet snapshots its container's size once, at construction — if that
  // size isn't final yet (common here since this card's height depends on
  // sibling facet controls collapsing/expanding), tiles only render for the
  // stale area. Same ResizeObserver + invalidateSize() workaround used by
  // the deleted MapPage.tsx and by the old MiniLocationPicker.
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const observer = new ResizeObserver(() => mapRef.current?.invalidateSize())
    observer.observe(el)
    return () => observer.disconnect()
  }, [mapRef, wrapperRef])
}

function pinIcon(color: string, Icon: typeof Zap): L.DivIcon {
  const size = 30
  const html = renderToStaticMarkup(
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
      }}
    >
      <Icon size={size * 0.55} color="#fff" strokeWidth={2.2} />
    </div>,
  )
  return L.divIcon({ html, className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2] })
}

function groupIcon(avatarUrl: string | null, name: string, isOpen: boolean): L.DivIcon {
  const size = 34
  const html = renderToStaticMarkup(
    <div style={{ position: 'relative', width: size, height: size }}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'hidden',
          border: '2px solid #fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
          background: 'var(--surface-2)',
        }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-faint)',
              fontWeight: 700,
              fontFamily: 'var(--font-sans)',
            }}
          >
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          insetInlineEnd: -2,
          insetBlockEnd: -2,
          width: 15,
          height: 15,
          borderRadius: '50%',
          background: 'var(--brand)',
          border: '2px solid #fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Users size={8} color="#fff" strokeWidth={3} />
      </div>
      {!isOpen && (
        <div
          style={{
            position: 'absolute',
            insetInlineStart: -2,
            insetBlockEnd: -2,
            width: 15,
            height: 15,
            borderRadius: '50%',
            background: 'var(--text-muted)',
            border: '2px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Lock size={8} color="#fff" strokeWidth={3} />
        </div>
      )}
    </div>,
  )
  return L.divIcon({ html, className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2] })
}

function pinKey(pin: MapPin): string {
  return pin.kind === 'group' ? `group-${pin.slug}` : `${pin.kind}-${pin.postId}`
}

function pinIconFor(pin: MapPin): L.DivIcon {
  if (pin.kind === 'action') return pinIcon('var(--action)', Zap)
  if (pin.kind === 'event') return pinIcon('var(--accent)', Calendar)
  return groupIcon(pin.avatarUrl, pin.name, pin.isOpen)
}

// The 'profile'/'address' location facet's reference point — a real,
// geographically-fixed Leaflet marker (unlike 'map_center' mode's pin,
// which is a screen-fixed CSS overlay that represents "wherever the map is
// currently centered," not a coordinate of its own). Deliberately distinct
// from pinIconFor's result-pin icons so it doesn't read as one more match.
function referenceMarkerIcon(): L.DivIcon {
  const size = 30
  const html = renderToStaticMarkup(<MapPinIcon size={size} fill="var(--brand)" color="var(--surface)" strokeWidth={1.5} />)
  return L.divIcon({ html, className: '', iconSize: [size, size], iconAnchor: [size / 2, size] })
}

function PinPopupContent({ pin, onAccess }: { pin: MapPin; onAccess: () => void }) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
        {pin.kind === 'event' ? pin.content : pin.kind === 'group' ? pin.name : pin.title}
      </div>

      {(pin.kind === 'action' || pin.kind === 'event') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Calendar size={13} color="var(--text-muted)" strokeWidth={2} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
            {formatDateTime(pin.startsAt, { autoHideTime: true })}
          </span>
        </div>
      )}

      {pin.kind === 'action' && pin.group.name && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <MapPinIcon size={13} color="var(--text-muted)" strokeWidth={2} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
            {pin.group.name}
          </span>
        </div>
      )}

      {pin.kind === 'event' && pin.placeText && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <MapPinIcon size={13} color="var(--text-muted)" strokeWidth={2} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
            {pin.placeText}
          </span>
        </div>
      )}

      {pin.kind === 'group' && pin.description && (
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          {pin.description}
        </div>
      )}

      <button
        type="button"
        onClick={onAccess}
        style={{
          marginTop: 4,
          padding: '5px 10px',
          border: 'none',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--brand)',
          color: 'var(--on-brand)',
          fontFamily: 'var(--font-sans)',
          fontWeight: 700,
          fontSize: 'var(--fs-xs)',
          cursor: 'pointer',
          alignSelf: 'flex-start',
        }}
      >
        {t('map.view_details')}
      </button>
    </div>
  )
}

function CenterTracker({ onChange }: { onChange: (lat: number, lng: number) => void }) {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter()
      onChange(center.lat, center.lng)
    },
  })
  return null
}

// Reports the current view (center + zoom) on every pan/zoom, independent
// of showCenterPicker/onCenterChange — this is "where is the user currently
// looking," not the search reference point. Feeds search.store.ts's
// mapCenter/mapZoom so returning from a detail page restores the exact
// view instead of resetting to the default.
function ViewTracker({ onChange }: { onChange: (center: [number, number], zoom: number) => void }) {
  const map = useMapEvents({
    moveend: () => onChange([map.getCenter().lat, map.getCenter().lng], map.getZoom()),
    zoomend: () => onChange([map.getCenter().lat, map.getCenter().lng], map.getZoom()),
  })
  return null
}

// Frames the current pin set instead of always sitting at initialCenter's
// default zoom — capped at FIT_BOUNDS_MAX_ZOOM so a single pin (or a
// tight cluster) doesn't zoom in absurdly close. Not mounted in
// showCenterPicker mode (see SearchResultsMap below): auto-framing there
// would fight the user's own drag-to-center gesture every time the radius
// search refetches after a move.
const FIT_BOUNDS_MAX_ZOOM = 14

// skipFirst: true when a restored view (search.store.ts's mapCenter/
// mapZoom) was passed in as this map's initial center/zoom — the first
// pins load after mount then leaves that restored view alone instead of
// immediately snapping it to the pins' bounding box. Any later pins change
// within the same mount (the user editing a facet) still re-fits normally.
// referencePoint (the 'profile'/'address' reference marker, when present)
// is included in the frame too — otherwise it could sit outside the view
// fit to the result pins alone, defeating the point of showing it.
function FitBoundsToPins({ pins, referencePoint, skipFirst }: { pins: MapPin[]; referencePoint: [number, number] | null; skipFirst: boolean }) {
  const map = useMap()
  const hasFitOnceRef = useRef(false)
  useEffect(() => {
    const points: [number, number][] = pins.map((p): [number, number] => [p.latitude, p.longitude])
    if (referencePoint) points.push(referencePoint)
    if (points.length === 0) return
    if (skipFirst && !hasFitOnceRef.current) {
      hasFitOnceRef.current = true
      return
    }
    hasFitOnceRef.current = true
    const bounds = L.latLngBounds(points)
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: FIT_BOUNDS_MAX_ZOOM })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, referencePoint, map])
  return null
}

// Ephemeral, client-only — centers/zooms the map on the browser's
// Geolocation API result. Never persisted anywhere (distinct from Profile
// Geolocation's opt-in, geocoded, stored address). Ported from the deleted
// MapPage.tsx's own LocateMeButton — same behavior, now living on the
// merged map instead of a standalone page. In showCenterPicker mode,
// flyTo() still fires CenterTracker's moveend listener once it settles, so
// this doubles as a quick way to set the radius search's reference point
// to the viewer's live position without dragging.
function LocateMeButton({ mapRef }: { mapRef: React.RefObject<L.Map | null> }) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isLocating, setIsLocating] = useState(false)

  function handleClick() {
    if (!navigator.geolocation) {
      toast.error(t('map.locate_unsupported'))
      return
    }
    // Geolocation is only available in a secure context (HTTPS, or
    // localhost) — on a plain-HTTP origin the browser refuses to even show
    // the permission prompt and reports it as PERMISSION_DENIED, which
    // reads as "you denied it" when really there was never a prompt to
    // answer.
    if (!window.isSecureContext) {
      toast.error(t('map.locate_insecure_context'))
      return
    }
    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsLocating(false)
        mapRef.current?.flyTo([position.coords.latitude, position.coords.longitude], LOCATE_ME_ZOOM)
      },
      (error) => {
        setIsLocating(false)
        // Distinguish the actual failure — "check your browser permission"
        // is misleading when the real cause is the device's location
        // services being off (POSITION_UNAVAILABLE) or a slow GPS fix
        // (TIMEOUT), neither of which the browser's permission prompt is
        // involved in at all.
        if (error.code === error.PERMISSION_DENIED) toast.error(t('map.locate_denied'))
        else if (error.code === error.POSITION_UNAVAILABLE) toast.error(t('map.locate_unavailable'))
        else toast.error(t('map.locate_timeout'))
      },
      { enableHighAccuracy: true, timeout: 10000 },
    )
  }

  return (
    <button
      type="button"
      aria-label={t('map.locate_me')}
      onClick={handleClick}
      disabled={isLocating}
      style={{
        position: 'absolute',
        insetInlineEnd: 12,
        bottom: 12,
        zIndex: 500,
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '1px solid var(--border)',
        background: 'var(--surface)',
        color: 'var(--text-muted)',
        boxShadow: 'var(--shadow-card)',
        cursor: isLocating ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isLocating ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <LocateFixed size={18} />}
    </button>
  )
}

// Collapsed state: a full-width button with a small, non-interactive
// Leaflet map as its background (real OSM tiles, not a guessed static
// image URL — guaranteed to render correctly since it reuses the exact
// tile source already proven elsewhere in the app) — a scrim + label sit
// on top so it reads as a button, not a broken map.
function CollapsedMapButton({ center, onClick }: { center: [number, number]; onClick: () => void }) {
  const { t } = useTranslation()
  const mapRef = useRef<L.Map | null>(null)
  const wrapperRef = useRef<HTMLButtonElement>(null)
  useInvalidateSizeOnResize(mapRef, wrapperRef)

  // Grid-stack, not position:absolute siblings — every child shares the
  // same single cell (grid-area 1/1), which overlays them unconditionally
  // regardless of Leaflet's own internal stacking-context/z-index rules.
  // The earlier absolute-positioned-overlay version rendered the label
  // below the map preview instead of on top of it — grid-stacking has no
  // ancestor-positioning dependency to get subtly wrong.
  // gridTemplateRows/Columns are pinned to an explicit 1fr rather than left
  // to auto — an auto track's size is computed from its items' content,
  // and every item here is itself percentage-sized (height:100%,
  // width:100%), a circular reference that resolved to a 0-size track (so
  // the button rendered as an empty box, its own text and map preview
  // invisible) until pinned to a definite size here.
  return (
    <button
      type="button"
      onClick={onClick}
      ref={wrapperRef}
      style={{
        display: 'grid',
        gridTemplateRows: '1fr',
        gridTemplateColumns: '1fr',
        width: '100%',
        height: 72,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        cursor: 'pointer',
        padding: 0,
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div style={{ gridArea: '1 / 1', zIndex: 0, pointerEvents: 'none' }}>
        <MapContainer
          ref={mapRef}
          center={center}
          zoom={PREVIEW_ZOOM}
          style={{ width: '100%', height: '100%' }}
          zoomControl={false}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        </MapContainer>
      </div>
      <div
        style={{
          gridArea: '1 / 1',
          zIndex: 1,
          background: 'linear-gradient(0deg, rgb(15 17 23 / 0.55), rgb(15 17 23 / 0.15))',
        }}
      />
      <div
        style={{
          gridArea: '1 / 1',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* A pill behind the icon+label, not just a text-shadow — the scrim
            gradient above isn't reliably dark enough over every OSM tile
            color (bright terrain, water, etc.), so legibility can't depend
            on it alone. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            borderRadius: 'var(--radius-pill)',
            background: 'rgb(15 17 23 / 0.72)',
            border: '1px solid rgb(255 255 255 / 0.25)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
          }}
        >
          <MapIcon size={18} color="#fff" strokeWidth={2.2} />
          <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: '#fff' }}>
            {t('search.map_show_button')}
          </span>
        </div>
      </div>
    </button>
  )
}

interface SearchResultsMapProps {
  pins: MapPin[]
  expanded: boolean
  onToggleExpanded: (expanded: boolean) => void
  showCenterPicker: boolean
  onCenterChange?: (lat: number, lng: number) => void
  initialCenter: [number, number]
  // Present once the caller has a restored view (search.store.ts's
  // mapZoom) — see FitBoundsToPins's skipFirst.
  initialZoom?: number
  onViewChange?: (center: [number, number], zoom: number) => void
  // The resolved 'profile'/'address' reference point, rendered as a real
  // marker (see referenceMarkerIcon) — null when locationMode is 'off' or
  // 'map_center' (that mode already shows its own screen-fixed pin) or
  // when 'address' hasn't resolved to coordinates yet.
  referencePoint?: [number, number] | null
}

export function SearchResultsMap({ pins, expanded, onToggleExpanded, showCenterPicker, onCenterChange, initialCenter, initialZoom, onViewChange, referencePoint = null }: SearchResultsMapProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const mapRef = useRef<L.Map | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  useInvalidateSizeOnResize(mapRef, wrapperRef)
  // Only reported once on mount so a radius search can be submitted without
  // requiring the user to drag first — same as MiniLocationPicker's rule.
  const [reportedInitial, setReportedInitial] = useState(false)

  useEffect(() => {
    if (showCenterPicker && onCenterChange && !reportedInitial) {
      onCenterChange(initialCenter[0], initialCenter[1])
      setReportedInitial(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCenterPicker])

  function handlePinClick(pin: MapPin) {
    navigate(pin.kind === 'group' ? `/groups/${pin.slug}` : `/posts/${pin.postId}`)
  }

  if (!expanded) {
    return <CollapsedMapButton center={initialCenter} onClick={() => onToggleExpanded(true)} />
  }

  return (
    <div>
      <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: 260, borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border)' }}>
        <MapContainer ref={mapRef} center={initialCenter} zoom={initialZoom ?? ISRAEL_DEFAULT_ZOOM} style={{ width: '100%', height: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MarkerClusterGroup chunkedLoading>
            {pins.map((pin) => (
              <Marker key={pinKey(pin)} position={[pin.latitude, pin.longitude]} icon={pinIconFor(pin)}>
                <Popup>
                  <PinPopupContent pin={pin} onAccess={() => handlePinClick(pin)} />
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
          {/* Outside the cluster group — it's the search's own reference
              point, not a result, so it should never merge into a cluster
              bubble alongside them. */}
          {!showCenterPicker && referencePoint && <Marker position={referencePoint} icon={referenceMarkerIcon()} />}
          {showCenterPicker && <CenterTracker onChange={(lat, lng) => onCenterChange?.(lat, lng)} />}
          {!showCenterPicker && <FitBoundsToPins pins={pins} referencePoint={referencePoint} skipFirst={initialZoom !== undefined} />}
          {onViewChange && <ViewTracker onChange={onViewChange} />}
        </MapContainer>
        {showCenterPicker && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              insetInlineStart: '50%',
              transform: 'translate(-50%, -100%)',
              pointerEvents: 'none',
              zIndex: 500,
            }}
          >
            <MapPinIcon size={32} fill="var(--brand)" color="var(--surface)" strokeWidth={1.5} />
          </div>
        )}
        <LocateMeButton mapRef={mapRef} />
      </div>
      <button
        type="button"
        onClick={() => onToggleExpanded(false)}
        style={{
          marginTop: 6,
          width: '100%',
          padding: '6px 0',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
          fontWeight: 700,
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-muted)',
        }}
      >
        {t('search.map_hide_button')}
      </button>
    </div>
  )
}
